import React, { useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    updateTrendsModal,
    updateTrendsDataOne,
    updateTrendsDataTwo,
} from "@features/modalSlice";
import { Modal } from "antd";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TREND_ATTRIBUTES, COLORS } from "@ts/constants";
import { selectChangeHandler } from "@ts/performance-overview";
import TrendsErrorComponent from "../error-boundaries/TrendsErrorComponent";
import {
    ITrendsData,
    ITrendsShow,
    IValueString,
    TTrendSeries,
} from "@ts/common-types";
import { AiOutlineClose } from "react-icons/ai";

interface ITrendsTitle {
    trendsTitle: IValueString;
}

const TrendsModal = React.memo(() => {
    const chartComponent = useRef(null);
    const dispatch = useDispatch();
    const show = useSelector(
        (state: ITrendsShow) => state.trendsModalDisp.value
    );
    const trendsTitle = useSelector(
        (state: ITrendsTitle) => state.trendsTitle.value
    );
    const trendsDataOne = useSelector(
        (state: ITrendsData) => state.trendsDataOne.value
    );
    const trendsDataTwo = useSelector(
        (state: ITrendsData) => state.trendsDataTwo.value
    );

    const options = useMemo(() => {
        return JSON.parse(
            JSON.stringify({
                credits: {
                    enabled: false,
                },
                colors: COLORS,
                title: {
                    text: "Trends",
                },
                accessibility: {
                    enabled: false,
                },
                xAxis: {
                    type: "datetime",
                    tickInterval: 3600 * 24 * 1000,
                    tickmarkPlacement: "between",
                },
                yAxis: [
                    {
                        title: {
                            text: "",
                        },
                        lineWidth: 1,
                        lineColor: COLORS[0],
                        labels: {
                            formatter: function (this: HTMLInputElement) {
                                return this.value;
                            },
                            style: {
                                color: COLORS[0],
                                fontSize: "13px",
                            },
                        },
                    },
                    {
                        title: {
                            text: "",
                        },
                        opposite: true,
                        lineWidth: 1,
                        lineColor: COLORS[1],
                        labels: {
                            formatter: function (this: HTMLInputElement) {
                                return this.value;
                            },
                            style: {
                                color: COLORS[1],
                                fontSize: "13px",
                            },
                        },
                    },
                ],
                tooltip: {
                    crosshairs: true,
                    shared: true,
                },
                plotOptions: {
                    spline: {
                        marker: {
                            radius: 4,
                            lineColor: "#666666",
                            lineWidth: 1,
                        },
                    },
                },
                series: [
                    trendsDataOne ? trendsDataOne : {},
                    trendsDataTwo ? trendsDataTwo : {},
                ],
            })
        );
    }, [trendsDataOne, trendsDataTwo]);

    const setShow = () => {
        dispatch(updateTrendsDataOne({} as TTrendSeries));
        dispatch(updateTrendsDataTwo({} as TTrendSeries));
        dispatch(updateTrendsModal(false));
    };

    useEffect(() => {
        if (show) {
            chartComponent.current.chart.reflow();
        }
    }, [show]);

    return (
        <>
            <Modal
                title={
                    <div className="d-flex align-items-center pb-3">
                        <h4>Trends - {trendsTitle}</h4>
                    </div>
                }
                open={show}
                onCancel={setShow}
                closeIcon={<AiOutlineClose style={{ fontSize: "35px" }} />}
                footer={[
                    <button className="btn btn-secondary" onClick={setShow}>
                        Close
                    </button>,
                ]}
                style={{ top: 20 }}
                width="95vw"
                styles={{
                    body: {
                        height: "72vh",
                        overflowY: "auto",
                    },
                }}
            >
                <div className="row chart-filter" id="trends-selector-id">
                    <div className="col">
                        <select
                            className="form-select form-select-sm form-select-xs"
                            data-index={0}
                            id={`select-${0}`}
                            onChange={(e) => selectChangeHandler(e, 0)}
                            defaultValue="impression"
                        >
                            <option key="choose">Choose</option>
                            {TREND_ATTRIBUTES.map((attr) => {
                                return (
                                    <option value={attr.key} key={attr.key}>
                                        {attr.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div className="col">
                        <select
                            className="form-select form-select-sm form-select-xs"
                            data-index={1}
                            id={`select-${1}`}
                            onChange={(e) => selectChangeHandler(e, 1)}
                            defaultValue="acos"
                        >
                            <option key="_choose">Choose</option>
                            {TREND_ATTRIBUTES.map((attr) => {
                                return (
                                    <option
                                        value={attr.key}
                                        key={"_" + attr.key}
                                    >
                                        {attr.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                </div>

                <div className="mt-3">
                    <div
                        id="chart-container"
                        className="modal-container-height"
                        style={{ overflow: "hidden" }}
                    >
                        <TrendsErrorComponent>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={options}
                                ref={chartComponent}
                                containerProps={{
                                    style: { height: "590px" },
                                }}
                            />
                        </TrendsErrorComponent>
                    </div>
                </div>
            </Modal>
        </>
    );
});

export default TrendsModal;
